import { useEffect, useState, useCallback } from "react";
import { useNavigate, Link } from "react-router-dom";
import { useAuth } from "@/lib/auth";
import api, { API, formatApiErrorDetail } from "@/lib/api";
import { toast } from "sonner";
import {
    LogOut, FileDown, Trash2, Mail, Phone, Calendar, RefreshCw, Search,
    Inbox, CheckCircle2, Clock, XCircle, Filter, Loader2, Home as HomeIcon, Activity,
    Megaphone, Flame,
} from "lucide-react";
import {
    Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription,
} from "@/components/ui/dialog";
import { AvailabilityPanel } from "@/components/admin/AvailabilityPanel";
import { ConstructionPanel } from "@/components/admin/ConstructionPanel";
import { BannerPanel } from "@/components/admin/BannerPanel";
import { WealthLeadsPanel } from "@/components/admin/WealthLeadsPanel";

const STATUS_OPTIONS = [
    { value: "neu", label: "Neu", className: "badge-neu" },
    { value: "in_bearbeitung", label: "In Bearbeitung", className: "badge-bearbeitung" },
    { value: "abgeschlossen", label: "Abgeschlossen", className: "badge-abgeschlossen" },
    { value: "verworfen", label: "Verworfen", className: "badge-verworfen" },
];

const USAGE_LABEL = {
    eigennutzung: "Eigennutzung",
    kapitalanlage: "Kapitalanlage",
    unentschieden: "Unentschieden",
};
const FINANCING_LABEL = {
    eigenkapital: "Eigenkapital vorhanden",
    finanzierung_benoetigt: "Finanzierung benötigt",
    bereits_finanziert: "Bereits geklärt",
    unentschieden: "Unentschieden",
};

const formatDate = (iso) => {
    if (!iso) return "-";
    try {
        return new Date(iso).toLocaleString("de-DE", {
            day: "2-digit", month: "2-digit", year: "numeric",
            hour: "2-digit", minute: "2-digit",
        });
    } catch { return iso; }
};

export default function AdminDashboard() {
    const { user, logout } = useAuth();
    const navigate = useNavigate();

    const [activeTab, setActiveTab] = useState("inquiries");
    const [inquiries, setInquiries] = useState([]);
    const [stats, setStats] = useState(null);
    const [loading, setLoading] = useState(true);
    const [filter, setFilter] = useState("all");
    const [search, setSearch] = useState("");
    const [selected, setSelected] = useState(null);
    const [adminNotes, setAdminNotes] = useState("");
    const [savingStatus, setSavingStatus] = useState(false);
    const [pdfLoadingId, setPdfLoadingId] = useState(null);

    const load = useCallback(async () => {
        setLoading(true);
        try {
            const params = filter !== "all" ? { status_filter: filter } : {};
            const [list, st] = await Promise.all([
                api.get("/inquiries", { params }),
                api.get("/inquiries/stats"),
            ]);
            setInquiries(list.data || []);
            setStats(st.data || null);
        } catch (err) {
            toast.error("Konnte Anfragen nicht laden", {
                description: formatApiErrorDetail(err.response?.data?.detail) || err.message,
            });
            if (err.response?.status === 401) {
                logout();
                navigate("/admin/login");
            }
        } finally {
            setLoading(false);
        }
    }, [filter, logout, navigate]);

    useEffect(() => { load(); }, [load]);

    useEffect(() => {
        if (selected) setAdminNotes(selected.admin_notes || "");
    }, [selected]);

    const filtered = inquiries.filter((q) => {
        if (!search) return true;
        const s = search.toLowerCase();
        return (
            (q.name || "").toLowerCase().includes(s) ||
            (q.email || "").toLowerCase().includes(s) ||
            (q.apartment_label || "").toLowerCase().includes(s) ||
            (q.phone || "").toLowerCase().includes(s)
        );
    });

    const handleStatus = async (newStatus, withNotes = false) => {
        if (!selected) return;
        setSavingStatus(true);
        try {
            const payload = { status: newStatus };
            if (withNotes) payload.admin_notes = adminNotes;
            const { data } = await api.patch(`/inquiries/${selected.id}/status`, payload);
            setSelected(data);
            await load();
            toast.success("Status aktualisiert");
        } catch (err) {
            toast.error("Status-Update fehlgeschlagen", {
                description: formatApiErrorDetail(err.response?.data?.detail) || err.message,
            });
        } finally {
            setSavingStatus(false);
        }
    };

    const handleDelete = async (id) => {
        if (!window.confirm("Anfrage wirklich löschen?")) return;
        try {
            await api.delete(`/inquiries/${id}`);
            toast.success("Anfrage gelöscht");
            setSelected(null);
            await load();
        } catch (err) {
            toast.error("Löschen fehlgeschlagen", {
                description: formatApiErrorDetail(err.response?.data?.detail) || err.message,
            });
        }
    };

    const handlePdf = async (inquiry) => {
        setPdfLoadingId(inquiry.id);
        try {
            const token = localStorage.getItem("g21_token");
            const res = await fetch(`${API}/inquiries/${inquiry.id}/pdf`, {
                headers: { Authorization: `Bearer ${token}` },
            });
            if (!res.ok) throw new Error("PDF konnte nicht erstellt werden");
            const blob = await res.blob();
            const url = window.URL.createObjectURL(blob);
            const a = document.createElement("a");
            a.href = url;
            const safeName = (inquiry.name || "anfrage").replace(/[^a-zA-Z0-9 _-]/g, "").replace(/\s+/g, "_");
            a.download = `G21_Anfrage_${safeName}_${inquiry.id.slice(0, 8)}.pdf`;
            document.body.appendChild(a);
            a.click();
            a.remove();
            window.URL.revokeObjectURL(url);
        } catch (err) {
            toast.error("PDF-Export fehlgeschlagen", { description: err.message });
        } finally {
            setPdfLoadingId(null);
        }
    };

    return (
        <div className="min-h-screen bg-cream" data-testid="admin-dashboard">
            <header className="bg-white border-b border-stone-200">
                <div className="container-page flex items-center justify-between h-16">
                    <Link to="/" className="flex items-baseline gap-2" data-testid="admin-logo">
                        <span className="font-heading text-xl text-ink">G21</span>
                        <span className="overline">Admin</span>
                    </Link>
                    <div className="flex items-center gap-4">
                        <span className="hidden sm:inline font-body text-sm text-stone-600" data-testid="admin-user-email">
                            {user?.email}
                        </span>
                        <button
                            onClick={() => { logout(); navigate("/"); }}
                            className="inline-flex items-center gap-2 px-4 py-2 border border-stone-300 hover:border-ink text-sm font-body"
                            data-testid="admin-logout"
                        >
                            <LogOut size={14} />
                            Abmelden
                        </button>
                    </div>
                </div>
            </header>

            <div className="container-page py-10">
                <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-4 mb-6">
                    <div>
                        <span className="overline">Admin-Bereich</span>
                        <h1 className="font-heading text-3xl sm:text-4xl mt-2 text-ink">Übersicht</h1>
                    </div>
                </div>

                <div className="inline-flex border border-stone-200 bg-white mb-8">
                    <TabBtn active={activeTab === "inquiries"} onClick={() => setActiveTab("inquiries")} testid="admin-tab-inquiries">
                        <Inbox size={14} />
                        Anfragen
                    </TabBtn>
                    <TabBtn active={activeTab === "availability"} onClick={() => setActiveTab("availability")} testid="admin-tab-availability">
                        <HomeIcon size={14} />
                        Verfügbarkeit
                    </TabBtn>
                    <TabBtn active={activeTab === "construction"} onClick={() => setActiveTab("construction")} testid="admin-tab-construction">
                        <Activity size={14} />
                        Bauphase
                    </TabBtn>
                    <TabBtn active={activeTab === "banners"} onClick={() => setActiveTab("banners")} testid="admin-tab-banners">
                        <Megaphone size={14} />
                        Werbung
                    </TabBtn>
                    <TabBtn active={activeTab === "wealth"} onClick={() => setActiveTab("wealth")} testid="admin-tab-wealth">
                        <Flame size={14} />
                        Vermögens-Leads
                    </TabBtn>
                </div>

                {activeTab === "availability" && <AvailabilityPanel />}
                {activeTab === "construction" && <ConstructionPanel />}
                {activeTab === "banners" && <BannerPanel />}
                {activeTab === "wealth" && <WealthLeadsPanel />}

                {activeTab === "inquiries" && (
                <>
                <div className="flex justify-end mb-4">
                    <button
                        onClick={load}
                        className="btn-ghost"
                        data-testid="admin-refresh"
                    >
                        <RefreshCw size={14} />
                        Aktualisieren
                    </button>
                </div>

                <div className="grid grid-cols-2 lg:grid-cols-5 gap-3 mb-8" data-testid="admin-stats">
                    <StatCard icon={Inbox} label="Gesamt" value={stats?.total ?? 0} accent="bg-ink text-white" />
                    <StatCard icon={Inbox} label="Neu" value={stats?.neu ?? 0} accent="bg-clay-100 text-brand-700" />
                    <StatCard icon={Clock} label="In Bearbeitung" value={stats?.in_bearbeitung ?? 0} accent="bg-blue-50 text-blue-800" />
                    <StatCard icon={CheckCircle2} label="Abgeschlossen" value={stats?.abgeschlossen ?? 0} accent="bg-green-50 text-green-800" />
                    <StatCard icon={XCircle} label="Verworfen" value={stats?.verworfen ?? 0} accent="bg-red-50 text-red-800" />
                </div>

                <div className="bg-white border border-stone-200">
                    <div className="flex flex-wrap items-center gap-3 px-5 py-4 border-b border-stone-200">
                        <div className="relative flex-1 min-w-[200px]">
                            <Search size={16} className="absolute left-3 top-3 text-stone-400" />
                            <input
                                value={search}
                                onChange={(e) => setSearch(e.target.value)}
                                placeholder="Name, E-Mail, Wohnung suchen…"
                                className="w-full pl-9 pr-3 py-2.5 bg-cream border border-stone-200 focus:outline-none focus:border-brand-500 font-body text-sm"
                                data-testid="admin-search"
                            />
                        </div>
                        <div className="flex items-center gap-2">
                            <Filter size={14} className="text-stone-500" />
                            <select
                                value={filter}
                                onChange={(e) => setFilter(e.target.value)}
                                className="px-3 py-2.5 bg-cream border border-stone-200 focus:outline-none focus:border-brand-500 font-body text-sm"
                                data-testid="admin-status-filter"
                            >
                                <option value="all">Alle Status</option>
                                {STATUS_OPTIONS.map((s) => (
                                    <option key={s.value} value={s.value}>{s.label}</option>
                                ))}
                            </select>
                        </div>
                    </div>

                    {loading ? (
                        <div className="p-12 flex items-center justify-center" data-testid="admin-loading">
                            <Loader2 className="animate-spin text-stone-400" size={24} />
                        </div>
                    ) : filtered.length === 0 ? (
                        <div className="p-16 text-center" data-testid="admin-empty">
                            <Inbox className="mx-auto text-stone-300 mb-4" size={42} />
                            <p className="font-body text-stone-500">Keine Anfragen gefunden.</p>
                        </div>
                    ) : (
                        <div className="overflow-x-auto">
                            <table className="w-full" data-testid="admin-inquiries-table">
                                <thead className="bg-cream border-b border-stone-200">
                                    <tr className="text-left">
                                        <Th>Name / Kontakt</Th>
                                        <Th>Wohnung</Th>
                                        <Th>Verwendung</Th>
                                        <Th>Eingang</Th>
                                        <Th>Status</Th>
                                        <Th className="text-right">Aktionen</Th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {filtered.map((q) => (
                                        <tr
                                            key={q.id}
                                            className="border-b border-stone-100 hover:bg-cream cursor-pointer"
                                            onClick={() => setSelected(q)}
                                            data-testid={`inquiry-row-${q.id}`}
                                        >
                                            <Td>
                                                <div className="font-body text-sm font-medium text-ink">{q.name}</div>
                                                <div className="font-body text-xs text-stone-500">{q.email}</div>
                                            </Td>
                                            <Td>
                                                <div className="font-body text-sm text-ink">{q.apartment_label}</div>
                                            </Td>
                                            <Td>
                                                <div className="font-body text-sm text-stone-600">
                                                    {USAGE_LABEL[q.usage_type] || q.usage_type}
                                                </div>
                                            </Td>
                                            <Td>
                                                <div className="font-body text-xs text-stone-600">
                                                    {formatDate(q.created_at)}
                                                </div>
                                            </Td>
                                            <Td>
                                                <StatusBadge status={q.status} />
                                            </Td>
                                            <Td className="text-right">
                                                <div className="inline-flex gap-2" onClick={(e) => e.stopPropagation()}>
                                                    <button
                                                        onClick={() => handlePdf(q)}
                                                        className="p-2 text-stone-600 hover:text-brand-600 hover:bg-cream"
                                                        aria-label="PDF herunterladen"
                                                        data-testid={`download-pdf-${q.id}`}
                                                        disabled={pdfLoadingId === q.id}
                                                    >
                                                        {pdfLoadingId === q.id ? (
                                                            <Loader2 size={15} className="animate-spin" />
                                                        ) : (
                                                            <FileDown size={15} />
                                                        )}
                                                    </button>
                                                    <button
                                                        onClick={() => handleDelete(q.id)}
                                                        className="p-2 text-stone-600 hover:text-red-600 hover:bg-cream"
                                                        aria-label="Löschen"
                                                        data-testid={`delete-inquiry-${q.id}`}
                                                    >
                                                        <Trash2 size={15} />
                                                    </button>
                                                </div>
                                            </Td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                    )}
                </div>
                </>
                )}
            </div>

            <Dialog open={Boolean(selected)} onOpenChange={(o) => !o && setSelected(null)}>
                <DialogContent className="max-w-3xl max-h-[92vh] overflow-y-auto" data-testid="inquiry-detail-dialog">
                    {selected && (
                        <>
                            <DialogHeader className="text-left">
                                <div className="flex items-center justify-between gap-4">
                                    <div>
                                        <span className="overline">Anfrage-Details</span>
                                        <DialogTitle className="font-heading text-2xl mt-1">{selected.name}</DialogTitle>
                                        <DialogDescription>
                                            Eingegangen am {formatDate(selected.created_at)}
                                        </DialogDescription>
                                    </div>
                                    <StatusBadge status={selected.status} />
                                </div>
                            </DialogHeader>

                            <div className="grid sm:grid-cols-2 gap-x-8 gap-y-5 mt-6">
                                <DetailRow icon={Mail} label="E-Mail" value={<a href={`mailto:${selected.email}`} className="hover:text-brand-600">{selected.email}</a>} />
                                <DetailRow icon={Phone} label="Telefon" value={selected.phone || "—"} />
                                <DetailRow label="Wohnung" value={selected.apartment_label} />
                                <DetailRow icon={Calendar} label="Wunsch-Einzug" value={selected.move_in_timing || "—"} />
                                <DetailRow label="Verwendung" value={USAGE_LABEL[selected.usage_type] || selected.usage_type} />
                                <DetailRow label="Finanzierung" value={FINANCING_LABEL[selected.financing] || selected.financing} />
                            </div>

                            {selected.message && (
                                <div className="mt-6">
                                    <div className="overline mb-2">Nachricht</div>
                                    <div className="bg-cream p-4 font-body text-sm text-stone-700 whitespace-pre-wrap">
                                        {selected.message}
                                    </div>
                                </div>
                            )}

                            <div className="mt-6">
                                <div className="overline mb-2">Interne Notizen</div>
                                <textarea
                                    rows={3}
                                    value={adminNotes}
                                    onChange={(e) => setAdminNotes(e.target.value)}
                                    className="w-full p-3 bg-cream border border-stone-200 focus:border-brand-500 focus:outline-none font-body text-sm"
                                    placeholder="Notizen für das Team…"
                                    data-testid="admin-notes-input"
                                />
                            </div>

                            <div className="mt-6 grid sm:grid-cols-2 gap-3">
                                <div className="flex flex-wrap gap-2">
                                    {STATUS_OPTIONS.map((s) => (
                                        <button
                                            key={s.value}
                                            onClick={() => handleStatus(s.value, true)}
                                            disabled={savingStatus}
                                            className={`px-3 py-1.5 text-xs font-body font-medium border transition-all ${
                                                selected.status === s.value
                                                    ? `${s.className} font-semibold`
                                                    : "bg-white border-stone-200 text-stone-600 hover:border-stone-400"
                                            } ${savingStatus ? "opacity-60" : ""}`}
                                            data-testid={`status-btn-${s.value}`}
                                        >
                                            {s.label}
                                        </button>
                                    ))}
                                </div>
                                <div className="flex justify-end gap-2">
                                    <button
                                        onClick={() => handlePdf(selected)}
                                        className="btn-secondary"
                                        data-testid="detail-download-pdf"
                                        disabled={pdfLoadingId === selected.id}
                                    >
                                        {pdfLoadingId === selected.id ? (
                                            <Loader2 size={14} className="animate-spin" />
                                        ) : (
                                            <FileDown size={14} />
                                        )}
                                        PDF
                                    </button>
                                    <button
                                        onClick={() => handleDelete(selected.id)}
                                        className="inline-flex items-center gap-2 px-5 py-3 border border-red-300 text-red-700 hover:bg-red-50 font-body text-sm"
                                        data-testid="detail-delete"
                                    >
                                        <Trash2 size={14} />
                                        Löschen
                                    </button>
                                </div>
                            </div>
                        </>
                    )}
                </DialogContent>
            </Dialog>
        </div>
    );
}

const StatCard = ({ icon: Icon, label, value, accent }) => (
    <div className="bg-white border border-stone-200 p-5 flex items-start justify-between">
        <div>
            <div className="overline text-stone-500">{label}</div>
            <div className="font-heading text-3xl text-ink mt-1">{value}</div>
        </div>
        <div className={`w-9 h-9 flex items-center justify-center ${accent}`}>
            <Icon size={16} strokeWidth={1.5} />
        </div>
    </div>
);

const Th = ({ children, className = "" }) => (
    <th className={`px-5 py-3 font-body text-xs font-semibold uppercase tracking-[0.15em] text-stone-500 ${className}`}>{children}</th>
);
const Td = ({ children, className = "" }) => (
    <td className={`px-5 py-4 align-middle ${className}`}>{children}</td>
);

const StatusBadge = ({ status }) => {
    const opt = STATUS_OPTIONS.find((s) => s.value === status) || STATUS_OPTIONS[0];
    return (
        <span className={`inline-flex items-center px-2.5 py-1 text-xs font-body font-medium ${opt.className}`}>
            {opt.label}
        </span>
    );
};

const DetailRow = ({ icon: Icon, label, value }) => (
    <div>
        <div className="overline text-stone-400 mb-1 flex items-center gap-1.5">
            {Icon && <Icon size={11} />}
            {label}
        </div>
        <div className="font-body text-sm text-ink">{value}</div>
    </div>
);


const TabBtn = ({ active, onClick, testid, children }) => (
    <button
        onClick={onClick}
        className={`inline-flex items-center gap-2 px-5 py-2.5 text-sm font-body font-medium transition-colors whitespace-nowrap ${
            active ? "bg-ink text-white" : "text-stone-600 hover:text-ink"
        }`}
        data-testid={testid}
    >
        {children}
    </button>
);
